/**
 * accountInfo.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Secured Communications. All rights reserved.
 *
**/

import React from "react";
import { Navigate } from "react-router-dom";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuIcon from "@mui/icons-material/Menu";
import { Alert, Checkbox, FormControlLabel, IconButton, Tabs, Tab, Tooltip } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";

import { ArchiveEnableContainer } from "./archiveEnableContainer.component";
import { ArchiveFileSearchContainer } from "./archiveFileSearchContainer.component";
import { ArchiveSearchContainer } from "./archiveSearchContainer.component";
import { UnlockArchiveComponent } from "./unlockArchive.component";

import { Account, Membership } from "../models/auth";

import { authServiceFactory } from "../services/auth.service";

export function AccountInfoContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);

  if (!authContext.membership) { return <Navigate to="/group" />; }

  const accountname = authContext.membership.accountname;

  return <div className="account-info-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
          disableRipple disableTouchRipple
          onClick={acctContext.toggleLeftNav}>
        <MenuIcon />
      </IconButton>
      <h3>Secure Archiving: {accountname}</h3>
      <span className="right-controls">
      </span>
    </header>
    <AccountInfoComponent membership={authContext.membership} />
  </div>;
}

interface Props {
  membership: Membership
}

function AccountInfoComponent(props: Props): React.ReactElement<any, any> {
  const [saving, setSaving] = React.useState<boolean>(false);
  const [account, setAccount] = React.useState<Account | null>(null);
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [currentTab, setCurrentTab] = React.useState<"settings" | "unlock" | "msgs" | "files">("settings");
  const [unlocked, setUnlocked] = React.useState<boolean>(false);
  const [pemPrivateKey, setPemPrivateKey] = React.useState<string>("");

  React.useEffect(function () {
    setPemPrivateKey("");
    setCurrentTab("settings");
    setUnlocked(false);
    authServiceFactory().getAccountInfo(props.membership.accountID).then(setAccount)
    .catch(err => {
      setErrMsg((err as Error).message);
    });
  }, [props.membership]);

  function changeTab(_event: React.SyntheticEvent, newValue: "settings" | "unlock" | "msgs" | "files") {
    setCurrentTab(newValue);
  }

  async function updateForcedArchiving(event: React.ChangeEvent<HTMLInputElement>) {
    if (!account || saving) { return; }
    const oldValue = account.forceGroupArchiving;
    try {
      const newValue = event.target.checked;
      setAccount({...account, forceGroupArchiving: newValue});
      await authServiceFactory().updateAccountInfo(
        account._id, {forceGroupArchiving: newValue});
      const mem = authServiceFactory().currentAccountMembership;
      if (mem) { mem.mustArchive = newValue; }
    } catch (err) {
      console.error("Error setting account info:", err);
      setErrMsg((err as Error).message);
      setAccount({...account, forceGroupArchiving: oldValue});
    } finally {
      setSaving(false);
    }
  }

  function updateAccount(key: string) {
    if (!account) { return; }
    setAccount({...account, kyberKey: key});
    const mem = authServiceFactory().currentAccountMembership;
    if (mem) { mem.canArchive = true; }
  }

  return <div className="account-info-inner-container">

    { errMsg && <Alert
        variant="filled"
        severity="error"
        onClick={() => setErrMsg("")}>{errMsg}</Alert> }

    { !account ? <></> : account.kyberKey ?

      <div className="archiving-enabled-container">
        <Tabs value={currentTab} onChange={changeTab} centered scrollButtons="auto">
          <Tab label="Settings" value="settings" disableFocusRipple disableRipple disableTouchRipple />
          <Tab label="Unlock" value="unlock" disableFocusRipple disableRipple disableTouchRipple />
          <Tab label="Messages" value="msgs" disabled={!unlocked} disableFocusRipple disableRipple disableTouchRipple />
          <Tab label="Files" value="files" disabled={pemPrivateKey === "" || !unlocked} disableFocusRipple disableRipple disableTouchRipple />
        </Tabs>

        {currentTab === "settings" && <section className="archive-settings-container">
          <h2>Archive Settings</h2>
          <form className="form-wrapper archive-settings-form">
            <FormControlLabel className="toggle-force-archiving"
              control={<Checkbox checked={account.forceGroupArchiving} onChange={updateForcedArchiving} />}
              label={<>
                  <span>Force archiving for all Groups/DMs</span>
                  <Tooltip title={"Ensures all groups and DMs are archiving messages and files. " +
                      (account.forceGroupArchiving ?
                        "If disabled, groups will still need to individually turn off archiving." :
                        "When enabled, chat will be disabled for all groups and DMs until those groups' owners enable archiving."
                      )}>
                    <HelpOutlineIcon />
                  </Tooltip>
                </>
              } />
            <p className="footnote">
              Note: it may take up to an hour for changes to be reflected in the UI.
            </p>
          </form>
        </section>}

        {currentTab === "unlock" &&
          <UnlockArchiveComponent
            pemPrivateKey={pemPrivateKey}
            unlocked={unlocked}
            setUnlocked={setUnlocked}
            setPemPrivateKey={setPemPrivateKey} />}

        {currentTab === "msgs" && <ArchiveSearchContainer />}
        {currentTab === "files" && pemPrivateKey &&
          <ArchiveFileSearchContainer pemPrivateKey={pemPrivateKey} />}

      </div> :

      <ArchiveEnableContainer
        accountID={account._id}
        onFinish={updateAccount} />

    }

  </div>;
}
